import {
  DeleteOutlined,
  EditOutlined,
  VerticalAlignTopOutlined,
} from '@ant-design/icons'
import { EJobPostingStatus, thunkActionLoading } from '@configs'
import { useAppDispatch, useAppSelector } from '@redux'
import { message, TableProps, Tooltip } from 'antd'
import { t } from 'i18next'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { careerLandingManagementAPI } from 'src/api/career-landing-management'
import { Loading } from 'src/common'
import { useDebounce } from 'src/hooks/useDebounce'
import {
  IJobPosting,
  IJobQuery,
} from 'src/interfaces/career-landing-management'
import {
  deleteJobAction,
  getJobAction,
} from 'src/redux/actions/career-landing-management'

export const useListJobTable = () => {
  const lastParams = useRef<IJobQuery>()
  const dispatch = useAppDispatch()
  const [deleteId, setDeleteId] = useState('')
  const [dateSorter, setDateSorter] = useState<string | undefined>()
  const [itemToTop, setItemToTop] = useState('')
  const [updateId, setUpdateId] = useState('')
  const [searchString, setSearchString] = useState<string>('')
  const debouncedValue = useDebounce<string | null>(searchString, 800)
  const jobs = useAppSelector((state) => state.careerLanding.jobs)
  const isGettingJobs = useAppSelector(
    (state) => state.careerLanding.loadings[thunkActionLoading.GET_JOB]
  )
  const isDeletingJob = useAppSelector(
    (state) => state.careerLanding.loadings[thunkActionLoading.DELETE_JOB]
  )

  const [totalJob, setTotalJob] = useState<number>()
  const [currentPage, setCurrentPage] = useState<number>()
  const [perPage, setPerpage] = useState<number>(10)
  const [status, setStatus] = useState<EJobPostingStatus | undefined>()
  const [isChangingPage, setIsChangingPage] = useState(false)

  const getListJob = async (params: IJobQuery) => {
    if (
      !isChangingPage &&
      JSON.stringify(params) === JSON.stringify(lastParams.current) &&
      !deleteId &&
      !updateId &&
      !itemToTop &&
      !dateSorter
    )
      return
    lastParams.current = params
    try {
      await careerLandingManagementAPI.upDateStatusPost()
      const res = await dispatch(getJobAction(params)).unwrap()
      if (res.success) {
        setTotalJob(res.data.total)
        setCurrentPage(res.data.page)
      }
    } catch (error: any) {
      if (error.message) {
        message.error({
          content: error.message,
        })
      }
    }
  }

  const callListJobAfterUpdate = async () => {
    if (debouncedValue || status) {
      handleClear()
    } else {
      await careerLandingManagementAPI.upDateStatusPost()
      getListJob({
        page: currentPage,
        limit: 10,
        sort: dateSorter ? `publishedDate:${dateSorter}` : undefined,
      })
    }
  }

  const onDeleteJob = async () => {
    try {
      if (deleteId) {
        const res = await dispatch(deleteJobAction(deleteId)).unwrap()
        if (res.success) {
          setDeleteId('')
          callListJobAfterUpdate()
          message.success({
            content: res?.message ?? t('common:delete_success'),
          })
        }
      }
    } catch (error: any) {
      if (error.message) {
        message.error({
          content: error.message,
        })
      }
    }
  }

  const onClickToTop = async (id: string) => {
    try {
      if (id) {
        setItemToTop(id)
        const res = await careerLandingManagementAPI.toTopJobPosting(id)
        if (res.success) {
          message.success({
            content: res?.message ?? t('common:save_success'),
          })
          callListJobAfterUpdate()
        }
        setItemToTop('')
      }
    } catch (error: any) {
      if (error.message) {
        message.error({
          content: error.message,
        })
      }
      setItemToTop('')
    }
  }

  useEffect(() => {
  
    getListJob({
      search: searchString?.trim() || undefined,
      status: status ?? undefined,
      sort: dateSorter ? `publishedDate:${dateSorter}` : undefined,
      page: 1,
      limit: perPage,
    })
  }, [status, dateSorter, itemToTop])

  useEffect(() => {
    
    getListJob({
      search: debouncedValue?.trim() || undefined,
      status: status ?? undefined,
      sort: dateSorter ? `publishedDate:${dateSorter}` : undefined,
      page: 1,
      limit: 10,
    })
  }, [debouncedValue])

  const handleClear = () => {
    setSearchString('')
    setStatus(undefined)
    setDateSorter(undefined)
  }

  const renderStatus = (status: EJobPostingStatus) => {
    switch (status) {
      case EJobPostingStatus.EXPIRED:
        return (
          <div className="bg-danger text-white w-fit px-4 py-2 rounded-md font-medium mx-auto">
            Expired
          </div>
        )
      case EJobPostingStatus.INACTIVE:
        return (
          <div className="bg-warning w-fit px-4 py-2 rounded-md font-medium mx-auto">
            Inactive
          </div>
        )
      case EJobPostingStatus.PUBLISHED:
        return (
          <div className="bg-success text-white w-fit px-4 py-2 rounded-md font-medium mx-auto">
            Published
          </div>
        )
    }
  }

  const columns: TableProps<IJobPosting>['columns'] = [
    {
      dataIndex: 'index',
      align: 'center',
      title: 'No',
      key: 'test',
      width: '4%',
    },
    {
      dataIndex: 'title',
      align: 'center',
      key: 'job_title',
      width: '4%',
      title: 'Job Title EN',
      render: (_, record) => {
        return (
          <p className="line-clamp-2 whitespace-pre-line m-0">{record.title}</p>
        )
      },
    },

    {
      dataIndex: 'type',
      align: 'center',
      key: 'job_type',
      width: '4%',
      title: 'Job Type',
    },

    {
      dataIndex: 'status',
      align: 'center',
      key: 'status',
      width: '4%',
      title: 'Status',
      render: (_, record) => {
        return renderStatus(record.status)
      },
    },
    {
      dataIndex: 'publishedDate',
      align: 'center',
      key: 'date',
      width: '4%',
      title: 'Published Date',
      // sorter: true,
      render: (_, record) => {
        return (
          <p className="line-clamp-2 m-0">
            {record?.publishedDate
              ? moment(record.publishedDate).format('MMMM DD, YYYY')
              : 'N/A'}
          </p>
        )
      },
    },

    {
      dataIndex: 'action',
      align: 'center',
      key: 'action',
      width: '4%',
      title: 'Action',
      render: (_, record) => {
        return (
          <div className="flex justify-center w-full space-x-10">
            <Tooltip title={'Click to top up'}>
              {itemToTop === record.id?.toString() ? (
                <Loading />
              ) : (
                <VerticalAlignTopOutlined
                  className={`text-lg font-light  cursor-pointer  hover:opacity-80 !text-success`}
                  onClick={() => onClickToTop(record.id?.toString() || '')}
                />
              )}
            </Tooltip>
            <Tooltip title={'Edit'}>
              <EditOutlined
                className={`text-lg font-light  cursor-pointer  hover:opacity-80 `}
                onClick={() => {
                  setUpdateId(record.id?.toString() || '')
                }}
              />
            </Tooltip>
            <Tooltip title={'Delete'}>
              <DeleteOutlined
                className={`text-lg font-light  cursor-pointer  hover:opacity-80 !text-red-600`}
                onClick={() => {
                  setDeleteId(record.id?.toString() || '')
                }}
              />
            </Tooltip>
          </div>
        )
      },
      ellipsis: true,
    },
  ]
  const handleTableChange: TableProps<any>['onChange'] = (
    pagination,
    filters,
    sorter
  ) => {
    if (!Array.isArray(sorter)) {
      if (sorter.order === 'ascend') {
        setDateSorter('ASC')
      } else if (sorter.order === 'descend') {
        setDateSorter('DESC')
      } else {
        setDateSorter(undefined)
      }
    }
  }

  const handlePaginationChange = async (page: number, pageSize: number) => {
    const nextPage = pageSize !== perPage ? 1 : page;
    if (pageSize !== perPage) {
      setPerpage(pageSize);
    }
    
    setIsChangingPage(true)
    
    try {
      await getListJob({
        page: nextPage,
        limit: pageSize,
        status: status ?? undefined,
        search: searchString?.trim() || undefined,
        sort: dateSorter ? `publishedDate:${dateSorter}` : undefined,
      });
    } finally {
      setIsChangingPage(false)
    }
  }

  return {
    jobs: jobs?.length ? jobs : [],
    columns,
    searchString,
    setSearchString,
    totalJob,
    getListJob,
    currentPage,
    isGettingJobs,
    setStatus,
    status,
    handleClear,
    deleteId,
    setDeleteId,
    onDeleteJob,
    isDeletingJob,
    updateId,
    setUpdateId,
    callListJobAfterUpdate,
    handleTableChange,
    setPerpage,
    perPage,
    setCurrentPage,
    handlePaginationChange
  }
}
